import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahfl4joh653ivfvnteg";

const CONTACT_ITEMS = [
  {
    icon: "ri-mail-send-line",
    label: "内容与线索",
    value: "editor@wnchigua.com",
    href: "mailto:editor@wnchigua.com",
  },
  {
    icon: "ri-question-answer-line",
    label: "合作与建议",
    value: "hello@wnchigua.com",
    href: "mailto:hello@wnchigua.com",
  },
  {
    icon: "ri-time-line",
    label: "整理与更新",
    value: "每日 09:00 - 23:00 实时滚动更新",
    href: "",
  },
];

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      params.append(key, value instanceof File ? value.name : String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const succeeded = response.ok && parsed?.code === "OK" && !isSpam;

      if (!succeeded) {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : "提交没有成功，请稍后重试，或直接发邮件给我们。"
        );
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络好像开小差了，请稍后再试一次。");
    }
  };

  return (
    <section id="contact" className="scroll-mt-20 bg-background-50 px-4 py-16 md:px-8 md:py-24">
      <Reveal>
        <SectionHeading
          eyebrow="Contact"
          title="留下联系方式，热点第一时间提醒你"
          description="订阅后我们会在重要话题升温时给你发一封简短的提醒，不发广告，随时可以取消。"
        />
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
        <Reveal>
          <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100/70 p-6 md:p-8">
            <h3 className="font-heading text-lg font-bold text-foreground-950">联系我们</h3>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
              有话题线索、内容纠错或合作想法，都可以通过下面的方式找到我们。线索越具体，被整理收录的效率越高。
            </p>

            <ul className="mt-6 space-y-4">
              {CONTACT_ITEMS.map((item) => (
                <li key={item.label} className="flex items-start gap-3.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-lg`} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="mt-0.5 block break-all text-sm font-medium text-foreground-900 transition-colors hover:text-primary-600"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="mt-0.5 text-sm font-medium text-foreground-900">
                        {item.value}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <p className="mt-auto flex items-center gap-2 pt-6 text-xs text-foreground-500">
              <i className="ri-shield-check-line text-secondary-600" aria-hidden="true" />
              我们只保存你主动提交的信息，不会用于其他用途。
            </p>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <form
            id="gossip-hotspot-subscribe"
            data-readdy-form
            onSubmit={handleSubmit}
            className="flex h-full flex-col rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-8"
          >
            <h3 className="font-heading text-lg font-bold text-foreground-950">热点订阅</h3>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
              填写下面的信息，我们会按你关注的方向推送热点提醒。
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <label
                  htmlFor="subscriber-name"
                  className="block text-xs font-medium text-foreground-700"
                >
                  昵称
                </label>
                <input
                  id="subscriber-name"
                  name="name"
                  type="text"
                  required
                  maxLength={40}
                  placeholder="怎么称呼你"
                  className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div>
                <label
                  htmlFor="subscriber-email"
                  className="block text-xs font-medium text-foreground-700"
                >
                  邮箱
                </label>
                <input
                  id="subscriber-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div>
                <label
                  htmlFor="subscriber-topic"
                  className="block text-xs font-medium text-foreground-700"
                >
                  感兴趣的方向
                </label>
                <select
                  id="subscriber-topic"
                  name="interest"
                  defaultValue="entertainment"
                  className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option value="entertainment">娱乐八卦</option>
                  <option value="screen">影视综</option>
                  <option value="society">社会热点</option>
                  <option value="tech">科技新鲜事</option>
                  <option value="lifestyle">生活方式</option>
                </select>
              </div>

              <input
                className="gossip-form-guard"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                    正在提交
                  </>
                ) : (
                  <>
                    <i className="ri-notification-3-line" aria-hidden="true" />
                    订阅热点提醒
                  </>
                )}
              </button>

              {status === "success" ? (
                <p
                  role="status"
                  className="flex items-center gap-2 rounded-md border border-secondary-300 bg-secondary-50 px-3.5 py-3 text-xs text-secondary-900"
                >
                  <i className="ri-checkbox-circle-line text-base" aria-hidden="true" />
                  订阅成功，热点升温时我们会第一时间提醒你。
                </p>
              ) : null}

              {status === "error" ? (
                <p
                  role="alert"
                  className="flex items-center gap-2 rounded-md border border-primary-300 bg-primary-100 px-3.5 py-3 text-xs text-primary-900"
                >
                  <i className="ri-error-warning-line text-base" aria-hidden="true" />
                  {formError}
                </p>
              ) : null}
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}